/**
 * 入力の途中で別の画面へ移ろうとしたときの問い（UNSAVED-LEAVE-ASK・`docs/10` §4「入力途中の移動」）。
 *
 * ★ここは**聞くだけ**。何が入力中か・保存するか・捨てるかは `confirmLeave`（`main.ts`）が決める。
 * ★`window.confirm` に頼らない: 確認用のブラウザの欄は `confirm` を出せず黙って「いいえ」を返し、
 *   押すたびに移動が止まった（2026-10-30）。`<dialog>` の無い端末だけ `confirm` に戻す。
 */
import { EASE_CLOSE, EASE_OPEN, motionEnabled } from "./fold-motion.js";

/**
 * 答え。★`saved` / `failed` は「保存して移動」の結果（保存の間はダイアログを開いたままにし、終わってから答える）。
 *   `stay` は「戻って続ける」・Esc・暗幕の外・`dismissLeaveDialog`。
 */
export type LeaveChoice = "saved" | "failed" | "discard" | "stay";

export interface LeaveQuestion {
  /** 何が入力中か（1行ずつ） */
  items: string[];
  /** ★「保存して移動」を出すか。出すとき `items` は1つ、`save` がある */
  canSave: boolean;
  /** 「保存して移動」の保存。成功なら true。★失敗の理由はここで画面に出す（ダイアログは出さない） */
  save?: () => Promise<boolean>;
}

export type AskLeave = (question: LeaveQuestion) => Promise<LeaveChoice>;

const SLIDE_PX = 8;
const IN_MS = 200;
const OUT_MS = 150;

const supportsDialog = (): boolean =>
  typeof HTMLDialogElement === "function" && typeof HTMLDialogElement.prototype.showModal === "function";

/** 出入りの動き。★動かせないとき（reduced-motion・`animate` 無し）はすぐ終わる */
function fade(dialog: HTMLDialogElement, entering: boolean): Promise<void> {
  if (!motionEnabled()) return Promise.resolve();
  const opts = { duration: entering ? IN_MS : OUT_MS, easing: entering ? EASE_OPEN : EASE_CLOSE, fill: entering ? ("backwards" as const) : ("both" as const) };
  return new Promise((resolve) => {
    const a = dialog.animate(
      entering
        ? [{ opacity: 0, transform: `translateY(${SLIDE_PX}px)` }, { opacity: 1, transform: "translateY(0)" }]
        : [{ opacity: 1 }, { opacity: 0 }],
      opts,
    );
    // 暗幕も同じ尺で（対応していない端末は例外になるので、ダイアログ本体だけにする）
    try {
      dialog.animate(entering ? [{ opacity: 0 }, { opacity: 1 }] : [{ opacity: 1 }, { opacity: 0 }], {
        ...opts,
        pseudoElement: "::backdrop",
      });
    } catch {
      /* 暗幕は動かさなくてよい */
    }
    a.onfinish = () => resolve();
    a.oncancel = () => resolve();
  });
}

function button(text: string, lead: boolean): HTMLButtonElement {
  const b = document.createElement("button");
  b.type = "button";
  b.textContent = text;
  // ★「保存せずに移動」に danger は付けない（docs/10 §4: 失われるのは記録になっていない打ちかけ）
  if (lead) b.className = "primary";
  return b;
}

/** 開いているダイアログを「戻って続ける」として閉じる関数。★ログイン画面へ送るとき（`dismissLeaveDialog`）に使う */
let dismiss: (() => void) | null = null;

/**
 * 開いているダイアログがあれば、「戻って続ける」として閉じる。
 * ★ログイン画面へ送られたあとにダイアログが残ると、前の人の入力の名前を出したまま画面を塞ぐ。
 */
export function dismissLeaveDialog(): void {
  dismiss?.();
}

export const askLeave: AskLeave = ({ items, canSave, save: saveFn }) => {
  if (!supportsDialog()) {
    return Promise.resolve(
      window.confirm(`保存していない${items.join("と")}があります。保存せずに移動しますか？`) ? "discard" : "stay",
    );
  }
  const origin = document.activeElement instanceof HTMLElement ? document.activeElement : null;
  const dialog = document.createElement("dialog");
  dialog.className = "leave-dialog";
  const title = document.createElement("h2");
  title.id = "leave-dialog-title";
  title.textContent = "保存していない入力があります";
  dialog.setAttribute("aria-labelledby", title.id);

  const body = document.createElement("div");
  body.className = "leave-dialog-body";
  body.append(title);
  if (canSave) {
    const p = document.createElement("p");
    p.textContent = `${items[0] ?? "入力中の値"}を保存してから移りますか？`;
    body.append(p);
  } else {
    const p = document.createElement("p");
    p.textContent = "保存せずに移動すると、次の入力は失われます。";
    const ul = document.createElement("ul");
    for (const item of items) {
      const li = document.createElement("li");
      li.textContent = item;
      ul.append(li);
    }
    // ★「保存して移動」を出せない理由と、その代わりの手順（複数・証憑・振り返りは、どれを保存するか黙って決められない）
    const why = document.createElement("p");
    why.textContent = "保存は、戻ってそれぞれの画面の保存ボタンから行ってください。";
    body.append(p, ul, why);
  }

  const save = canSave ? button("保存して移動", true) : null;
  const discard = button("保存せずに移動", false);
  const stay = button("戻って続ける", !canSave);
  const actions = document.createElement("div");
  actions.className = "leave-dialog-actions";
  actions.append(...(save === null ? [] : [save]), discard, stay);
  // ★保存の間だけ出す。`role="status"` は差し込んだ時点で読み上げられる
  const progress = document.createElement("p");
  progress.setAttribute("role", "status");
  body.append(actions, progress);
  dialog.append(body);
  document.body.append(dialog);

  return new Promise<LeaveChoice>((resolve) => {
    let done = false;
    let saving = false;
    const finish = (choice: LeaveChoice): void => {
      if (done) return;
      done = true;
      dismiss = null;
      void fade(dialog, false).then(() => {
        dialog.close();
        dialog.remove();
        // ★閉じたら押した元へ（移動する/しないはその後で呼び出し側が決める）。
        //   ★失敗のときは戻さない: 理由を出したボタンへ呼び出し側がフォーカスを置く
        if (choice !== "failed" && origin?.isConnected === true) origin.focus();
        resolve(choice);
      });
    };
    dismiss = () => {
      saving = false;
      finish("stay");
    };
    /*
     * ★★保存の間はダイアログを開いたままにする。閉じると、押したのに何も起きないまま数秒たつ。
     *   3つのボタンを押せなくし、Esc・暗幕でも閉じない。終わったら成功も失敗も閉じる（失敗の理由は画面側）。
     */
    save?.addEventListener("click", () => {
      if (saving || saveFn === undefined) return;
      saving = true;
      for (const b of actions.querySelectorAll("button")) b.disabled = true;
      dialog.setAttribute("aria-busy", "true");
      progress.textContent = "保存しています…";
      void saveFn().then(
        (ok) => finish(ok ? "saved" : "failed"),
        () => finish("failed"),
      );
    });
    discard.addEventListener("click", () => finish("discard"));
    stay.addEventListener("click", () => finish("stay"));
    // Esc。★標準の閉じ方に任せず、動きを挟んで同じ出口から出す
    dialog.addEventListener("cancel", (event) => {
      event.preventDefault();
      if (!saving) finish("stay");
    });
    // 暗幕の外。★中身は `.leave-dialog-body` が埋めるので、`dialog` 自身への click は暗幕だけ
    dialog.addEventListener("click", (event) => {
      if (event.target === dialog && !saving) finish("stay");
    });
    dialog.showModal();
    (save ?? stay).focus();
    void fade(dialog, true);
  });
};
